import { Check, Filter, X } from 'lucide-react';
import * as React from 'react';

import { Badge } from '@/components/ui/catalyst/badge';
import { Button } from '@/components/ui/catalyst/button';
import {
  Dropdown,
  DropdownButton,
  DropdownDivider,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from '@/components/ui/catalyst/dropdown';
import { Input } from '@/components/ui/catalyst/input';
import { cn } from '@/lib/utils';

interface FilterOption {
  id: string;
  name: string;
}

interface FilterPreset {
  name: string;
  values: string[];
}

interface ColumnHeaderFilterProps {
  label: string;
  options?: FilterOption[];
  selectedValues?: string[];
  onSelectionChange?: (values: string[]) => void;
  value?: string | null;
  onChange?: (value: string | null) => void;
  multiSelect?: boolean;
  searchable?: boolean;
  clearable?: boolean;
  presets?: FilterPreset[];
}

export const ColumnHeaderFilter = React.memo<ColumnHeaderFilterProps>(
  ({
    label,
    options = [],
    selectedValues = [],
    onSelectionChange,
    value,
    onChange,
    multiSelect = false,
    searchable = true,
    clearable = true,
    presets = [],
  }) => {
    const [searchQuery, setSearchQuery] = React.useState('');
    const isFiltered = multiSelect
      ? selectedValues.length > 0
      : value !== null && value !== undefined && value !== '';

    const filteredOptions = React.useMemo(() => {
      if (!searchQuery) return options;
      return options.filter((option) =>
        option.name.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }, [options, searchQuery]);

    const handleSingleSelect = React.useCallback(
      (optionId: string | null) => {
        if (onChange) {
          onChange(optionId);
        }
      },
      [onChange],
    );

    const handleMultiSelect = React.useCallback(
      (optionId: string) => {
        if (onSelectionChange) {
          const newSelection = selectedValues.includes(optionId)
            ? selectedValues.filter((v) => v !== optionId)
            : [...selectedValues, optionId];
          onSelectionChange(newSelection);
        }
      },
      [selectedValues, onSelectionChange],
    );

    const handleClear = React.useCallback(() => {
      if (multiSelect && onSelectionChange) {
        onSelectionChange([]);
      } else if (onChange) {
        onChange(null);
      }
      setSearchQuery('');
    }, [multiSelect, onSelectionChange, onChange]);

    const handlePresetSelect = React.useCallback(
      (presetValues: string[]) => {
        if (multiSelect && onSelectionChange) {
          onSelectionChange(presetValues);
        }
      },
      [multiSelect, onSelectionChange],
    );

    return (
      <Dropdown>
        <DropdownButton
          as={Button}
          plain
          className={cn(
            'flex items-center gap-1 font-medium',
            isFiltered && 'text-blue-600 dark:text-blue-400',
          )}
        >
          {label}
          {isFiltered && (
            <Badge color="blue" className="ml-1">
              {multiSelect ? selectedValues.length : '1'}
            </Badge>
          )}
          <Filter
            className={cn(
              'h-3 w-3',
              isFiltered ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400',
            )}
          />
        </DropdownButton>

        <DropdownMenu
          className="min-w-[200px] max-h-[400px] p-2 block!"
          anchor="bottom start"
        >
          {searchable && (
            <>
              <div className="px-2 pb-2">
                <Input
                  type="text"
                  placeholder={`Search ${label.toLowerCase()}...`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full"
                />
              </div>
              <DropdownDivider />
            </>
          )}

          {multiSelect && presets && presets.length > 0 && (
            <>
              <div className="px-2 pb-2">
                <div className="text-xs font-medium text-gray-500 mb-2">
                  Quick filters:
                </div>
                <div className="flex flex-wrap gap-1">
                  {presets.map((preset) => {
                    const isPresetActive =
                      preset.values.length === selectedValues.length &&
                      preset.values.every((presetValue) =>
                        selectedValues.includes(presetValue),
                      );

                    return (
                      <button
                        key={preset.name}
                        type="button"
                        onClick={() => handlePresetSelect(preset.values)}
                        className={cn(
                          'px-2 py-1 text-xs rounded border transition-colors',
                          isPresetActive
                            ? 'bg-blue-100 border-blue-300 text-blue-700'
                            : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100',
                        )}
                      >
                        {preset.name}
                      </button>
                    );
                  })}
                </div>
              </div>
              <DropdownDivider />
            </>
          )}

          <div className="max-h-[250px] overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">
                No options found
              </div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = multiSelect
                  ? selectedValues.includes(option.id)
                  : value === option.id;

                return (
                  <DropdownItem
                    key={option.id}
                    onClick={() =>
                      multiSelect
                        ? handleMultiSelect(option.id)
                        : handleSingleSelect(option.id)
                    }
                    className="w-full flex! flex-row justify-start"
                  >
                    {multiSelect && (
                      <Check
                        className={cn(
                          'size-4',
                          isSelected ? 'opacity-100' : 'opacity-0',
                        )}
                        data-slot="icon"
                      />
                    )}
                    <DropdownLabel className="w-full">
                      {option.name}
                    </DropdownLabel>
                  </DropdownItem>
                );
              })
            )}
          </div>

          {clearable && isFiltered && (
            <>
              <DropdownDivider />
              <DropdownItem
                onClick={handleClear}
                className="w-full flex! flex-row justify-center"
              >
                <X className="size-4" data-slot="icon" />
                <DropdownLabel>Clear filter</DropdownLabel>
              </DropdownItem>
            </>
          )}
        </DropdownMenu>
      </Dropdown>
    );
  },
);

ColumnHeaderFilter.displayName = 'ColumnHeaderFilter';
